import { LocalDate } from '@js-joda/core';

import { CompleteAddress } from '@dreryk/address-common-api';
import {
   CurrentHealthcareProviderInvoicePdfQuery,
   ExternalSystemBusinessFault,
   FiscalDocumentProviderAuthorizationFault,
   PrivateIndividualInvoiceCreateCommand,
} from '@dreryk/egabinet-invoice-frontend-api';
import { Money } from '@dreryk/money-common-api';

import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';
import { createInterpolatedTranslationKey } from '@dreryk/react-common';

import { invoiceCreateModalTranslationKeys } from '../translations';
import { InvoicePaymentMethod } from '../types';

import { InvoiceCreateFormBean } from './form/InvoiceCreateForm.types';
import { getMedicalServiceName } from './form/servicesSection/InvoiceItemsTable.utils';
import { downloadInvoiceAsPdf } from './queries/queries';
import { invoiceTypeVisitor } from './form/InvoiceCreateForm.utils';

type PrivateIndividualInvoiceCreateCommandOptions = {
   onSuccess: VoidFunction;
   paymentMethod: InvoicePaymentMethod;
   visitDate: LocalDate;
   visitSettlementId: number;
   receiptAvailableForInvoice: boolean;
};

export const usePrivateIndividualInvoiceCreateCommand = (
   options: PrivateIndividualInvoiceCreateCommandOptions
): UseCqrsCommandResult<
   void,
   InvoiceCreateFormBean,
   FiscalDocumentProviderAuthorizationFault | ExternalSystemBusinessFault
> =>
   useCqrsCommand<void, InvoiceCreateFormBean, FiscalDocumentProviderAuthorizationFault | ExternalSystemBusinessFault>(
      (parameters) => {
         const command = new PrivateIndividualInvoiceCreateCommand();
         const completeAddress = CompleteAddress.builder()
            .withCountry(parameters.country)
            .withTown(parameters.town!)
            .withPostcode(parameters.postcode!)
            .withStreet(parameters.street)
            .withBuilding(parameters.building!)
            .withFlat(parameters.flat)
            .build();

         command.address = completeAddress;
         command.buyerFirstName = parameters.firstName!;
         command.buyerLastName = parameters.lastName!;
         command.description = Choice.withUseDefaultIfNull(parameters.description);
         command.fiscalItems = parameters.invoiceItems.map((item) => ({
            name: item.invoiceParameters.nameForInvoicing ?? getMedicalServiceName(item.name)!,
            quantity: item.quantity!,
            unit: item.unit!,
            unitPrice: Money.of(item.unitPrice!),
            taxRate: item.taxRate!,
         }));
         command.paymentMethod = options.paymentMethod.type;
         command.paymentMethodDescription = Choice.withUseDefaultIfNull(options.paymentMethod.description ?? null);
         command.paymentTo = parameters.paymentDate;
         command.sellDate = options.visitDate;
         command.taxExemptionBasis = parameters.taxExemptionBasis;
         command.referenceId = options.visitSettlementId.toString();

         invoiceTypeVisitor(options.receiptAvailableForInvoice)({
            withReceipt: () => {
               command.receiptReferenceId = Choice.withUseDefaultIfNull(options.visitSettlementId.toString());
            },
            default: () => null,
         });

         return command;
      },
      {
         onSuccess: async () => {
            dispatchSnackbarPositiveEvent(invoiceCreateModalTranslationKeys.invoiceCreateSuccess);
            await downloadInvoiceAsPdf(options.visitSettlementId.toString())();
            refetchCqrsQuery(CurrentHealthcareProviderInvoicePdfQuery);
            options.onSuccess();
         },
         onFault: {
            'egabinet-invoice-frontend-api:FiscalDocumentProviderAuthorizationFault': () => {
               dispatchSnackbarNegativeEvent(invoiceCreateModalTranslationKeys.apiTokenFault);
            },

            'egabinet-invoice-frontend-api:ExternalSystemBusinessFault': ({ externalSystemErrorMessage }) => {
               dispatchSnackbarNegativeEvent(
                  createInterpolatedTranslationKey(invoiceCreateModalTranslationKeys.externalSystemBusinessFault, {
                     messageFromFakturownia: externalSystemErrorMessage,
                  })
               );
            },
         },
      }
   );
